import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";

const emptyCommunication = {
  title: "",
  message: "",
  audience: "ALL",
  priority: "NORMAL",
  delivery_status: "DRAFT",
  scheduled_for: "",
  send_email: false,
  attachment: null,
  remove_attachment: false,
};

const numberFormat = new Intl.NumberFormat();

function HeadmasterOffice({
  officeLabel = "The Head's Office",
  officeContext = "HEAD",
}) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [communications, setCommunications] = useState([]);
  const [communicationForm, setCommunicationForm] = useState(emptyCommunication);
  const [editingCommunication, setEditingCommunication] = useState(null);
  const [viewCommunication, setViewCommunication] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadHeadmasterOffice = async () => {
    try {
      setLoading(true);
      const [summaryResponse, communicationsResponse] = await Promise.all([
        axiosInstance.get("headmaster/summary/"),
        axiosInstance.get(
          `headmaster/announcements/?office=${officeContext}`
        ),
      ]);
      setSummary(summaryResponse.data || {});
      setCommunications(communicationsResponse.data || []);
    } catch (error) {
      console.error(error);
      toast.error(`Failed to load ${officeLabel}.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHeadmasterOffice();
  }, []);

  const people = summary?.people || {};
  const approvals = summary?.approvals || {};
  const academic = summary?.academic || {};
  const teacherWorkload = summary?.teacher_workload || [];
  const classGroups = summary?.class_groups || [];
  const recentAccounts = summary?.recent_accounts || [];
  const upcomingEvents = summary?.upcoming_events || [];
  const upcomingTrips = summary?.upcoming_trips || [];

  const myCommunications = communications.filter(
    (item) => item.office === officeContext && item.can_manage
  );

  const leadershipCommunications = communications.filter(
    (item) => item.office !== officeContext || !item.can_manage
  );
  const visibleMyCommunications = myCommunications.slice(0, 5);
  const visibleLeadershipCommunications = leadershipCommunications.slice(0, 5);
  const scheduledCount = myCommunications.filter((item) => item.delivery_status === "SCHEDULED").length;
  const draftCount = myCommunications.filter((item) => item.delivery_status === "DRAFT").length;
  const sentCount = myCommunications.filter((item) => item.delivery_status === "SENT" || item.email_sent).length;

  const stats = useMemo(
    () => [
      { label: "Students", value: people.students || 0, icon: "bi-mortarboard", tone: "#125b2d" },
      { label: "Teachers", value: people.teachers || 0, icon: "bi-person-workspace", tone: "#144e76" },
      { label: "Parents", value: people.parents || 0, icon: "bi-people", tone: "#6d28d9" },
      { label: "Open Approvals", value: (approvals.pending_teachers || 0) + (approvals.pending_parent_claims || 0), icon: "bi-hourglass-split", tone: "#b45309" },
    ],
    [approvals.pending_parent_claims, approvals.pending_teachers, people.parents, people.students, people.teachers]
  );

  const openComposer = (communication = null) => {
    if (
      communication &&
      (communication.office !== officeContext || !communication.can_manage)
    ) {
      toast.info("This communication belongs to another leadership office and is read-only here.");
      setViewCommunication(communication);
      return;
    }

    if (communication) {
      setEditingCommunication(communication);
      setCommunicationForm({
        title: communication.title || "",
        message: communication.message || "",
        audience: communication.audience || "ALL",
        priority: communication.priority || "NORMAL",
        delivery_status: communication.delivery_status || "DRAFT",
        scheduled_for: toDateTimeInputValue(communication.scheduled_for),
        send_email: false,
        attachment: null,
        remove_attachment: false,
      });
    } else {
      setEditingCommunication(null);
      setCommunicationForm(emptyCommunication);
    }

    setActiveModal("compose");
  };

  const handleCommunicationChange = (event) => {
    const { name, value, type, checked, files } = event.target;
    setCommunicationForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "file"
          ? (files?.[0] || null)
          : value,
    }));
  };

  const saveCommunication = async (event) => {
    event.preventDefault();

    if (!communicationForm.title.trim() || !communicationForm.message.trim()) {
      toast.warning("Communication title and message are required.");
      return;
    }

    if (communicationForm.delivery_status === "SCHEDULED" && !communicationForm.scheduled_for) {
      toast.warning("Choose a date and time for scheduled communication.");
      return;
    }

    try {
      setSaving(true);
      const payload = new FormData();
      payload.append("title", communicationForm.title);
      payload.append("message", communicationForm.message);
      payload.append("audience", communicationForm.audience);
      payload.append("priority", communicationForm.priority);
      payload.append("delivery_status", communicationForm.delivery_status);
      payload.append("scheduled_for", communicationForm.scheduled_for || "");
      payload.append("send_email", communicationForm.send_email ? "true" : "false");
      payload.append("remove_attachment", communicationForm.remove_attachment ? "true" : "false");
      payload.append("office", officeContext);
      if (communicationForm.attachment) {
        payload.append("attachment", communicationForm.attachment);
      }

      const config = { headers: { "Content-Type": "multipart/form-data" } };
      const response = editingCommunication
        ? await axiosInstance.patch(`headmaster/announcements/${editingCommunication.id}/`, payload, config)
        : await axiosInstance.post("headmaster/announcements/", payload, config);

      const saved = response.data.announcement;
      setCommunications((prev) => {
        const exists = prev.some((item) => item.id === saved.id);
        if (exists) {
          return prev.map((item) => (item.id === saved.id ? saved : item));
        }
        return [saved, ...prev];
      });
      setActiveModal(null);
      setEditingCommunication(null);
      setCommunicationForm(emptyCommunication);
      toast.success(response.data.message || "Communication saved successfully.");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to save communication.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCommunication = async (communication) => {
    if (
      !communication?.can_manage ||
      communication?.office !== officeContext
    ) {
      toast.info("Only the office that created this communication can delete it.");
      return;
    }

    if (!window.confirm(`Delete "${communication.title}"?`)) return;

    try {
      await axiosInstance.delete(
        `headmaster/announcements/${communication.id}/?office=${officeContext}`
      );
      setCommunications((prev) => prev.filter((item) => item.id !== communication.id));
      setViewCommunication(null);
      toast.success("Communication deleted successfully.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete communication.");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h3 className="fw-bold mb-1" style={{ color: "#125b2d" }}>{officeLabel}</h3>
          <p className="text-muted mb-0">Leadership oversight, approvals, and school communication.</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button className="btn btn-success" onClick={() => openComposer()}>
            <i className="bi bi-send me-2"></i>
            Compose Communication
          </button>
          <button className="btn btn-outline-success" onClick={loadHeadmasterOffice}>
            <i className="bi bi-arrow-clockwise me-2"></i>
            Refresh
          </button>
        </div>
      </div>

      {loading && (
        <div className="alert alert-light border">
          <span className="spinner-border spinner-border-sm me-2"></span>
          Loading {officeLabel}...
        </div>
      )}

      <div className="row g-3 mb-4">
        {stats.map((item) => (
          <div className="col-md-6 col-xl-3" key={item.label}>
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted fw-bold">{item.label}</small>
                  <h3 className="fw-bold mb-0">{numberFormat.format(item.value)}</h3>
                </div>
                <div className="rounded-3 p-3 text-white" style={{ background: item.tone }}>
                  <i className={`bi ${item.icon} fs-3`}></i>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4">
        <div className="col-xl-8">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <h5 className="mb-0">Leadership Work Queue</h5>
                <small className="text-muted">Senior actions that need attention.</small>
              </div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <ActionTile title="Pending Teachers" value={`${approvals.pending_teachers || 0} awaiting approval`} icon="bi-person-plus" onClick={() => navigate("/teachers")} />
                <ActionTile title="Parent Claims" value={`${approvals.pending_parent_claims || 0} student claims pending`} icon="bi-link-45deg" onClick={() => navigate("/parents")} />
                <ActionTile title="Academic Overview" value={`${academic.teacher_allocations || 0} teacher allocations`} icon="bi-mortarboard" onClick={() => setActiveModal("academic")} />
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <h5 className="mb-0">Trips & Upcoming Events</h5>
                <small className="text-muted">A leadership view of planned school activities and educational visits.</small>
              </div>
              <button className="btn btn-sm btn-outline-success" onClick={() => navigate("/events")}>Manage Events</button>
            </div>
            <div className="card-body">
              <div className="row g-3 mb-3">
                <SummaryPill label="Upcoming Events" value={upcomingEvents.length} />
                <SummaryPill label="School Trips" value={upcomingTrips.length} />
                <SummaryPill label="Consent Required" value={upcomingEvents.filter((item) => item.requires_consent).length} />
              </div>
              {upcomingEvents.length === 0 ? (
                <div className="text-muted small">No upcoming trips or events have been scheduled.</div>
              ) : (
                <div className="list-group list-group-flush">
                  {upcomingEvents.slice(0, 6).map((item) => (
                    <div className="list-group-item px-0" key={item.id}>
                      <div className="d-flex justify-content-between gap-3">
                        <div>
                          <div className="fw-semibold">{item.title}</div>
                          <small className="text-muted">{item.event_type === "TRIP" ? "Trip / Educational Visit" : item.event_type?.replaceAll("_", " ")} · {item.location || "Venue TBA"}</small>
                        </div>
                        <div className="text-end">
                          <div className="fw-semibold">{item.start_date}</div>
                          <small className="text-muted">{item.start_time || "Time TBA"}</small>
                        </div>
                      </div>
                      {(item.requires_consent || item.transport_required) && (
                        <div className="mt-2 d-flex gap-2">
                          {item.requires_consent && <span className="badge text-bg-warning">Consent required</span>}
                          {item.transport_required && <span className="badge text-bg-info">Transport required</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white d-flex justify-content-between align-items-center gap-2">
              <div>
                <h5 className="mb-0">My Communications</h5>
                <small className="text-muted">
                  Create and manage only communications published from {officeLabel}.
                </small>
              </div>
              <button className="btn btn-sm btn-success" onClick={() => openComposer()}>
                <i className="bi bi-plus-lg me-1"></i>
                New
              </button>
            </div>
            <div className="card-body">
              <div className="row g-3 mb-3">
                <SummaryPill label="Sent" value={sentCount} />
                <SummaryPill label="Scheduled" value={scheduledCount} />
                <SummaryPill label="Drafts" value={draftCount} />
                <SummaryPill label="Mine" value={myCommunications.length} />
              </div>

              <div className="list-group list-group-flush border rounded-3">
                {visibleMyCommunications.map((item) => (
                  <button key={item.id} className="list-group-item list-group-item-action" onClick={() => setViewCommunication(item)}>
                    <div className="d-flex flex-wrap justify-content-between gap-2">
                      <div>
                        <div className="fw-bold">{item.title}</div>
                        <small className="text-muted">
                          {item.source || officeLabel} · {formatCommunicationMeta(item)}
                        </small>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <StatusBadge status={item.delivery_status} />
                        <span className="badge text-bg-light border">{item.audience}</span>
                      </div>
                    </div>
                  </button>
                ))}
                {myCommunications.length === 0 && (
                  <div className="text-center text-muted py-4">
                    No communications created by {officeLabel} yet.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white">
              <h5 className="mb-0">Leadership Communications</h5>
              <small className="text-muted">
                Quick view of newsletters and notices from other leadership offices. These are read-only.
              </small>
            </div>
            <div className="card-body">
              <div className="list-group list-group-flush border rounded-3">
                {visibleLeadershipCommunications.map((item) => (
                  <button key={item.id} className="list-group-item list-group-item-action" onClick={() => setViewCommunication(item)}>
                    <div className="d-flex flex-wrap justify-content-between gap-2">
                      <div>
                        <div className="fw-bold">{item.title}</div>
                        <small className="text-muted">
                          Sent by {item.source || item.created_by || "School"} · {formatCommunicationMeta(item)}
                        </small>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <span className="badge text-bg-secondary">READ ONLY</span>
                        <StatusBadge status={item.delivery_status} />
                        <span className="badge text-bg-light border">{item.audience}</span>
                      </div>
                    </div>
                  </button>
                ))}
                {leadershipCommunications.length === 0 && (
                  <div className="text-center text-muted py-4">
                    No communications from other leadership offices.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <PreviewCard
                title="Academic Oversight"
                icon="bi-building-check"
                description={`${academic.class_groups || 0} class groups, ${academic.class_subjects || 0} class-subject links`}
                button="View Academic Details"
                onClick={() => setActiveModal("academic")}
              />
            </div>
            <div className="col-md-6">
              <PreviewCard
                title="Teacher Allocations"
                icon="bi-diagram-3"
                description={`${teacherWorkload.length} teachers in the current snapshot`}
                button="View Teacher Snapshot"
                onClick={() => setActiveModal("teachers")}
              />
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white">
              <h5 className="mb-0">Communication Guide</h5>
            </div>
            <div className="card-body">
              <StepItem number="1" title="Draft" text="Write the message and choose the audience." />
              <StepItem number="2" title="Schedule or Send" text="Save as draft, schedule it, or send immediately by email." />
              <StepItem number="3" title="Manage" text="Edit or delete only your own office communications. Other leadership newsletters remain read-only." />
            </div>
          </div>

          <PreviewCard
            title="Recent Accounts"
            icon="bi-person-lines-fill"
            description={`${recentAccounts.length} latest system accounts`}
            button="View Accounts"
            onClick={() => setActiveModal("accounts")}
          />
        </div>
      </div>

      {activeModal === "compose" && (
        <CommunicationFormModal
          formData={communicationForm}
          editingCommunication={editingCommunication}
          saving={saving}
          onChange={handleCommunicationChange}
          onSubmit={saveCommunication}
          onClose={() => {
            setActiveModal(null);
            setEditingCommunication(null);
            setCommunicationForm(emptyCommunication);
          }}
        />
      )}

      {viewCommunication && (
        <CommunicationViewModal
          communication={viewCommunication}
          onClose={() => setViewCommunication(null)}
          onEdit={(item) => {
            setViewCommunication(null);
            openComposer(item);
          }}
          onDelete={deleteCommunication}
        />
      )}

      {activeModal === "academic" && (
        <AcademicModal academic={academic} classGroups={classGroups} onClose={() => setActiveModal(null)} />
      )}

      {activeModal === "teachers" && (
        <TeacherSnapshotModal teachers={teacherWorkload} onClose={() => setActiveModal(null)} />
      )}

      {activeModal === "accounts" && (
        <RecentAccountsModal accounts={recentAccounts} onClose={() => setActiveModal(null)} />
      )}
    </div>
  );
}

function ActionTile({ title, value, icon, onClick }) {
  return (
    <div className="col-md-4">
      <button className="btn btn-light border w-100 text-start p-3 h-100" onClick={onClick}>
        <i className={`bi ${icon} text-success me-2`}></i>
        <span className="fw-bold d-block">{title}</span>
        <span className="text-muted">{value}</span>
      </button>
    </div>
  );
}

function SummaryPill({ label, value }) {
  return (
    <div className="col-6 col-md-3">
      <div className="border rounded-3 p-3 bg-light h-100">
        <small className="text-muted fw-bold">{label}</small>
        <div className="fw-bold mt-1">{value}</div>
      </div>
    </div>
  );
}

function PreviewCard({ title, icon, description, button, onClick }) {
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="d-flex align-items-start gap-3">
          <div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}>
            <i className={`bi ${icon} fs-4`}></i>
          </div>
          <div>
            <h5 className="fw-bold mb-1">{title}</h5>
            <p className="text-muted mb-3">{description}</p>
            <button className="btn btn-outline-success btn-sm" onClick={onClick}>{button}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepItem({ number, title, text }) {
  return (
    <div className="d-flex gap-3 mb-3">
      <div className="rounded-circle bg-success text-white fw-bold d-flex align-items-center justify-content-center" style={{ width: 34, height: 34, flexShrink: 0 }}>{number}</div>
      <div>
        <div className="fw-bold">{title}</div>
        <small className="text-muted">{text}</small>
      </div>
    </div>
  );
}

function CommunicationFormModal({ formData, editingCommunication, saving, onChange, onSubmit, onClose }) {
  const isScheduled = formData.delivery_status === "SCHEDULED";
  const isSendNow = formData.delivery_status === "SENT";

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold">{editingCommunication ? "Edit Communication" : "Compose Communication"}</h5>
              <small className="text-muted">Choose the audience, delivery method, and message details.</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={onSubmit}>
            <div className="modal-body" style={{ maxHeight: "72vh", overflowY: "auto" }}>
              <div className="mb-3">
                <label className="form-label">Title</label>
                <input name="title" className="form-control" value={formData.title} onChange={onChange} placeholder="e.g. Staff briefing reminder" />
              </div>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Audience</label>
                  <select name="audience" className="form-select" value={formData.audience} onChange={onChange}>
                    <option value="ALL">Everyone</option>
                    <option value="TEACHERS">Teachers</option>
                    <option value="PARENTS">Parents</option>
                    <option value="STUDENTS">Students</option>
                    <option value="STAFF">Staff</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Priority</label>
                  <select name="priority" className="form-select" value={formData.priority} onChange={onChange}>
                    <option value="NORMAL">Normal</option>
                    <option value="IMPORTANT">Important</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Delivery</label>
                  <select name="delivery_status" className="form-select" value={formData.delivery_status} onChange={onChange}>
                    <option value="DRAFT">Save as draft</option>
                    <option value="SCHEDULED">Schedule</option>
                    <option value="SENT">Send now</option>
                  </select>
                </div>
              </div>

              {isScheduled && (
                <div className="mt-3">
                  <label className="form-label">Scheduled Date & Time</label>
                  <input type="datetime-local" name="scheduled_for" className="form-control" value={formData.scheduled_for} onChange={onChange} />
                </div>
              )}

              <div className="my-3">
                <label className="form-label">Message</label>
                <textarea name="message" className="form-control" rows="7" value={formData.message} onChange={onChange} placeholder="Write the communication clearly here..."></textarea>
              </div>

              <div className="mb-3">
                <label className="form-label">Newsletter / Attachment</label>
                <input
                  type="file"
                  name="attachment"
                  className="form-control"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.mp4,.webm,.mov,.m4v"
                  onChange={onChange}
                />
                <small className="text-muted d-block mt-1">PDF, Word, image or video. Maximum 15 MB.</small>
                {editingCommunication?.attachment_name && !formData.attachment && (
                  <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
                    <span className="badge text-bg-light border">Current: {editingCommunication.attachment_name}</span>
                    <label className="form-check mb-0">
                      <input
                        type="checkbox"
                        name="remove_attachment"
                        className="form-check-input"
                        checked={formData.remove_attachment}
                        onChange={onChange}
                      />
                      <span className="form-check-label small">Remove current attachment</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="alert alert-success py-2 small">
                <i className="bi bi-bell me-2"></i>
                Send now creates an in-app notification for the selected audience. Email is optional.
              </div>

              <label className={`form-check border rounded-3 p-3 ${isSendNow ? "bg-light" : ""}`}>
                <input type="checkbox" name="send_email" className="form-check-input" checked={formData.send_email} onChange={onChange} disabled={!isSendNow} />
                <span className="form-check-label fw-semibold ms-2">Send by email now</span>
                <small className="d-block text-muted ms-4">Email is only sent when delivery is set to Send now.</small>
              </label>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn btn-success" disabled={saving}>{saving ? "Saving..." : editingCommunication ? "Update Communication" : "Save Communication"}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function CommunicationViewModal({ communication, onClose, onEdit, onDelete }) {
  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold">{communication.title}</h5>
              <small className="text-muted">
                {communication.source || communication.created_by || "School Communication"}
                {" · "}
                {formatCommunicationMeta(communication)}
              </small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{ maxHeight: "72vh", overflowY: "auto" }}>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <StatusBadge status={communication.delivery_status} />
              <span className="badge text-bg-light border">{communication.audience}</span>
              <span className="badge text-bg-light border">{communication.priority}</span>
              {communication.email_sent && <span className="badge text-bg-success">Email sent</span>}
            </div>
            <div className="border rounded-3 p-3 bg-light mb-3" style={{ whiteSpace: "pre-wrap" }}>{communication.message}</div>
            {communication.attachment_url && (
              <a
                href={communication.attachment_url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-success mb-3"
              >
                <i className="bi bi-paperclip me-2"></i>
                Open {communication.attachment_name || "attachment"}
              </a>
            )}
            {communication.email_error && <div className="alert alert-warning">{communication.email_error}</div>}
          </div>
          <div className="modal-footer">
            <button className="btn btn-light" onClick={onClose}>Close</button>
            {communication.can_manage ? (
              <>
                <button className="btn btn-outline-success" onClick={() => onEdit(communication)}>
                  <i className="bi bi-pencil-square me-2"></i>Edit / Reschedule
                </button>
                <button className="btn btn-outline-danger" onClick={() => onDelete(communication)}>
                  <i className="bi bi-trash me-2"></i>Delete
                </button>
              </>
            ) : (
              <span className="badge text-bg-secondary align-self-center">
                Read only - managed by {communication.source || "the originating office"}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AcademicModal({ academic, classGroups, onClose }) {
  return (
    <DataModal title="Academic Oversight" onClose={onClose}>
      <div className="row g-3 mb-3">
        <SummaryPill label="Active Year" value={academic.active_year || "Not set"} />
        <SummaryPill label="Active Term" value={academic.active_term || "Not set"} />
        <SummaryPill label="Class Groups" value={academic.class_groups || 0} />
        <SummaryPill label="Class Subjects" value={academic.class_subjects || 0} />
      </div>
      <DataTable
        columns={["Class Group", "Class Teacher", "Subjects", "Capacity", "Status"]}
        rows={classGroups.map((group) => [
          group.name,
          group.class_teacher || "Not assigned",
          group.subjects_count || 0,
          group.capacity || 0,
          group.is_active ? "Active" : "Inactive",
        ])}
        empty="No class groups configured yet."
      />
    </DataModal>
  );
}

function TeacherSnapshotModal({ teachers, onClose }) {
  return (
    <DataModal title="Teacher Allocation Snapshot" onClose={onClose}>
      <DataTable
        columns={["Teacher", "Staff No.", "Allocations", "Status"]}
        rows={teachers.map((teacher) => [
          teacher.full_name,
          teacher.staff_number,
          teacher.allocations_count,
          teacher.status,
        ])}
        empty="No teachers found."
      />
    </DataModal>
  );
}

function RecentAccountsModal({ accounts, onClose }) {
  return (
    <DataModal title="Recent Accounts" onClose={onClose}>
      <DataTable
        columns={["Name", "Email", "Type", "Status"]}
        rows={accounts.map((account) => [
          account.full_name,
          account.email,
          account.user_type,
          account.status,
        ])}
        empty="No recent accounts."
      />
    </DataModal>
  );
}

function DataModal({ title, children, onClose }) {
  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{ maxHeight: "72vh", overflowY: "auto" }}>{children}</div>
          <div className="modal-footer"><button className="btn btn-light" onClick={onClose}>Close</button></div>
        </div>
      </div>
    </div>
  );
}

function DataTable({ columns, rows, empty }) {
  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light"><tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={columns.length} className="text-center text-muted py-4">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }) {
  const normalized = status || "DRAFT";
  const className = normalized === "SENT" ? "text-bg-success" : normalized === "SCHEDULED" ? "text-bg-warning" : "text-bg-secondary";
  return <span className={`badge ${className}`}>{normalized}</span>;
}

function formatCommunicationMeta(item) {
  if (item.delivery_status === "SCHEDULED" && item.scheduled_for) {
    return `Scheduled for ${new Date(item.scheduled_for).toLocaleString()}`;
  }
  if (item.sent_at) {
    return `Sent ${new Date(item.sent_at).toLocaleString()}`;
  }
  if (item.created_at) {
    return `Created ${new Date(item.created_at).toLocaleString()}`;
  }
  return "No date available";
}

function toDateTimeInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

export default HeadmasterOffice;
